import { forwardRef } from 'react';
import { type LinkProps as RouterLinkProps } from 'react-router-dom';
import MuiLink, { LinkProps as MuiLinkProps } from '@mui/material/Link';

import { focusRing } from 'src/theme/css';

import { Link as RouterLink } from './link';

// ----------------------------------------------------------------------

type Props = Omit<MuiLinkProps, 'href'> & Pick<RouterLinkProps, 'to'>;

/**
 * Makes a whole `Card` open a page while the card holds exactly one link (its title).
 * An `::after` overlay stretches the link over the card — the nearest positioned ancestor —
 * so a click anywhere opens it, and Tab stops on the card once with the focus ring around it.
 *
 * Other controls inside the card must sit above the overlay: give them
 * `position: 'relative', zIndex: 1`.
 */
export const CardLink = forwardRef<HTMLAnchorElement, Props>(({ to, sx, ...other }, ref) => (
  <MuiLink
    ref={ref}
    component={RouterLink}
    to={to}
    color="inherit"
    underline="none"
    sx={[
      (theme) => ({
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          // Matches the Card's own radius, so the ring follows the card's rounded corners.
          borderRadius: `${theme.shape.borderRadius * 2}px`,
        },
        // The overlay, not the title text, carries the ring — so it outlines the whole card.
        '&:focus-visible, &.Mui-focusVisible': { outline: 'none' },
        '&:focus-visible::after, &.Mui-focusVisible::after': focusRing(theme, 'inset'),
      }),
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    {...other}
  />
));

CardLink.displayName = 'CardLink';
